"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ArrowUpDown, HandCoins } from "lucide-react";
import {
  BillLinReceivableSchema,
  COLUMN_WIDTH_STATUS,
  FilterBar,
  DataTablePaginationSkeleton,
  DataTableSkeleton,
  StatusBadge,
  buildDataTableThemeVars,
  cn,
  columnWidthPercents,
  resolveColumnWidth,
  type ColumnSchema,
  type TableFilters,
  type WorkerSchema,
} from "@aera/data-table";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { useBindFinancialWorkSearch, useFinancialWorkSearch } from "@/app/financial-work/FinancialWorkSearchContext";
import { ConnectionLinkButton } from "@/app/components/billin/ConnectionLinkButton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/Tooltip";
import {
  useWorkspaceReceivableDetails,
  type ReceivableListItem,
} from "@/hooks/useWorkspaceReceivableDetails";
import type { Workspace } from "@/lib/api";
import type { ReceivableKind } from "@/lib/pendingOrders";
import { summarizeReceivableItemsByKind } from "@/lib/pendingOrders";
import { getReceivableOrderId } from "@/lib/pendingOrders";
import type { ReceivableTabKind } from "@/lib/billinConstants";
import {
  buildReceivableTableColumns,
  getReceivableExpiresAtMs,
  resolveReceivableTableFieldValue,
} from "@/lib/pendingOrders";
import { resolveReceivableDisplayStatus } from "@/lib/receivableDisplayStatus";
import {
  createDefaultReceivableFilters,
  filterAndSortReceivableItems,
} from "@/lib/receivableFilters";
import { isReceivableArchivedDisplayStatus, filterReceivableMoneyCandidates } from "@/lib/receivableDisplayStatus";
import { refetchReceivablePendingWithRetry } from "@/lib/receivablePendingRefresh";
import {
  ReceivableFieldCell,
  ReceivableRecordDrawer,
} from "./ReceivableRecordDrawer";
import { receivableFieldCellType } from "./receivableDetailsUtils";

const RECEIVABLE_LINE_HINT =
  "มีรายการค้างอยู่ เปิดแชตบิลลินในไลน์เพื่อจัดการและปิดยอดที่ค้างได้เลยครับ";

const RECEIVABLE_TABLE_THEME = buildDataTableThemeVars();

const RECEIVABLE_PAGE_SIZE = 50;

const RECEIVABLE_AMOUNT_FIELD_KEYS = new Set([
  "slip_amount",
  "amount",
  "amount_original",
  "amount_allocated",
  "amount_paid",
  "amount_remaining",
  "transfer_amount",
  "total_amount",
]);

const RECEIVABLE_TABLE_HEAD_CLASS =
  "h-10 px-3 text-[11px] font-bold text-aera-lightslate uppercase tracking-wider align-middle whitespace-nowrap";
const RECEIVABLE_TABLE_CELL_CLASS =
  "h-[36.5px] max-h-[36.5px] overflow-hidden px-3 py-0 align-middle text-sm font-medium text-aera-gray transition-colors group-hover:text-aera-charcoal";
const RECEIVABLE_TABLE_ROW_CLASS =
  "h-[36.5px] max-h-[36.5px] border-b border-aera-lightgray last:border-b-0 transition-colors group hover:bg-aera-lightgray/50 cursor-pointer";
const RECEIVABLE_ROW_EXPIRY_BG_THRESHOLD_MS = 1 * 60 * 60 * 1000;
const RECEIVABLE_ROW_EXPIRY_BLINK_THRESHOLD_MS = 5 * 60 * 1000;
const RECEIVABLE_ROW_EXPIRY_BG_CLASS = "bg-aera-redlight/30 hover:bg-aera-redlight/40";
const RECEIVABLE_ROW_EXPIRY_BLINK_CLASS = "receivable-row-expiry-blink";

function replaceUrlWithoutNavigation(url: URL) {
  try {
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl === currentUrl) return;
    window.history.replaceState(null, "", nextUrl);
  } catch {
    // ignore
  }
}

function ReceivableStatusBadges({
  item,
}: {
  item: ReceivableListItem;
}) {
  const label = resolveReceivableDisplayStatus(item);
  return (
    <div className="flex shrink-0 items-center gap-1 whitespace-nowrap">
      <StatusBadge status={label} />
    </div>
  );
}

function getReceivableFieldValue(
  item: ReceivableListItem,
  key: string,
): string {
  return resolveReceivableTableFieldValue(item, key);
}

function ReceivableTableShell({
  children,
  minWidth,
  columnPercents,
  columnKeys,
  fillViewportHeight = true,
}: {
  children: ReactNode;
  minWidth: string;
  columnPercents?: number[];
  columnKeys?: string[];
  fillViewportHeight?: boolean;
}) {
  return (
    <div
      className={
        fillViewportHeight
          ? "relative min-h-0 flex-1 overflow-x-auto overflow-y-auto custom-scrollbar bg-aera-white max-[899px]:h-auto max-[899px]:flex-none max-[899px]:overflow-x-auto max-[899px]:overflow-y-visible"
          : "relative h-auto shrink-0 overflow-x-auto custom-scrollbar bg-aera-white"
      }
    >
      <table
        className="w-full border-collapse table-fixed"
        style={{ minWidth }}
      >
        {columnPercents?.length ? (
          <colgroup>
            {columnPercents.map((percent, index) => (
              <col
                key={columnKeys?.[index] ?? index}
                style={{ width: `${percent}%` }}
              />
            ))}
          </colgroup>
        ) : null}
        {children}
      </table>
    </div>
  );
}

function ReceivableTableHead({ children }: { children: ReactNode }) {
  return (
    <thead className="sticky top-0 z-5">
      <tr className="bg-aera-white backdrop-blur-md border-b border-aera-lightgray">
        {children}
      </tr>
    </thead>
  );
}

function ReceivableTablePagination({
  pageIndex,
  totalItems,
  onPageChange,
}: {
  pageIndex: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / RECEIVABLE_PAGE_SIZE));
  const canGoNext = pageIndex + 1 < totalPages;

  return (
    <div className="shrink-0 border-t border-aera-lightgray bg-aera-white backdrop-blur-md rounded-b-2xl">
      <div className="flex items-center justify-between gap-3 px-3 md:px-4 py-3 text-[11px] font-bold tracking-[0.08em] uppercase text-aera-gray">
        <div className="flex items-center gap-2">
          <span>{`หน้า ${pageIndex + 1}`}</span>
          <span className="text-aera-gray/70">{`/ ${totalPages}`}</span>
          <span className="text-aera-gray/70">{`(แสดง ${RECEIVABLE_PAGE_SIZE}/หน้า)`}</span>
        </div>
        <div className="flex items-center gap-2">
          <AeraButton
            variant="outline"
            size="xs"
            fontWeight="bold"
            textSize="xs"
            disabled={pageIndex <= 0}
            onClick={() => onPageChange(Math.max(pageIndex - 1, 0))}
            className="uppercase"
          >
            ก่อนหน้า
          </AeraButton>
          <AeraButton
            variant="outline"
            size="xs"
            fontWeight="bold"
            textSize="xs"
            disabled={!canGoNext}
            onClick={() =>
              onPageChange(Math.min(pageIndex + 1, totalPages - 1))
            }
            className="uppercase"
          >
            ถัดไป
          </AeraButton>
        </div>
      </div>
    </div>
  );
}

function getSortableColumnIds(schema: WorkerSchema): Set<string> {
  return new Set(
    schema.columns
      .filter((column) => column.sortable)
      .map((column) => column.id),
  );
}

function resolveReceivableColumnAlign(
  columnKey: string,
  schema: WorkerSchema,
): "left" | "right" {
  const schemaColumn = schema.columns.find((column) => column.id === columnKey);
  if (schemaColumn?.align === "right") return "right";
  if (schemaColumn?.type === "amount") return "right";
  if (RECEIVABLE_AMOUNT_FIELD_KEYS.has(columnKey)) return "right";
  if (receivableFieldCellType(columnKey) === "amount") return "right";
  return "left";
}

const SLIP_STATUS_COLUMN_WIDTH = 200;
/** Announcement often shows two badges (ค้าง + รอตรวจ) — 140 clips them. */
const ANNOUNCEMENT_STATUS_COLUMN_WIDTH = 232;

function resolveReceivableColumnWidth(
  columnKey: string,
  schema: WorkerSchema,
  receivableKind: ReceivableKind,
): number {
  if (columnKey === "__status__") {
    if (receivableKind === "announcement") return ANNOUNCEMENT_STATUS_COLUMN_WIDTH;
    if (receivableKind === "slip") return SLIP_STATUS_COLUMN_WIDTH;
    return COLUMN_WIDTH_STATUS;
  }

  const schemaColumn = schema.columns.find((column) => column.id === columnKey);
  if (schemaColumn) return resolveColumnWidth(schemaColumn);

  const cellType = receivableFieldCellType(columnKey);
  const typeByCell: Record<string, ColumnSchema["type"]> = {
    amount: "amount",
    datetime: "datetime",
    bank: "bank",
    tel: "tel",
    reference: "text",
    expiry: "text",
    image: "image",
    text: "text",
  };

  return resolveColumnWidth({
    type: typeByCell[cellType] ?? "text",
  });
}

function ReceivableDynamicTable({
  items,
  pageIndex,
  receivableKind,
  schema,
  filters,
  onSort,
  onRowClick,
  fillViewportHeight = true,
}: {
  items: ReceivableListItem[];
  pageIndex: number;
  receivableKind: ReceivableKind;
  schema: WorkerSchema;
  filters: TableFilters;
  onSort: (colId: string) => void;
  onRowClick?: (item: ReceivableListItem) => void;
  fillViewportHeight?: boolean;
}) {
  const statusFilter = filters.status?.trim() ?? "";
  // ยอดค้าง table never shows ครบกำหนด; expired archive also skips urgency tint.
  const hideExpiryUrgency = statusFilter === "หมดอายุ";
  const columns = useMemo(
    () => buildReceivableTableColumns(items, receivableKind),
    [items, receivableKind],
  );
  const sortableColumnIds = useMemo(
    () => getSortableColumnIds(schema),
    [schema],
  );
  const columnWidths = useMemo(
    () =>
      columns.map((column) =>
        resolveReceivableColumnWidth(column.key, schema, receivableKind),
      ),
    [columns, schema, receivableKind],
  );
  const columnPercents = useMemo(
    () => columnWidthPercents(columnWidths),
    [columnWidths],
  );
  const minWidthPx = Math.max(
    columnWidths.reduce((total, width) => total + width, 0) + 16,
    720,
  );
  const pageStart = pageIndex * RECEIVABLE_PAGE_SIZE;
  const pageItems = items.slice(pageStart, pageStart + RECEIVABLE_PAGE_SIZE);

  return (
    <ReceivableTableShell
      minWidth={`${minWidthPx}px`}
      columnPercents={columnPercents}
      columnKeys={columns.map((column) => column.key)}
      fillViewportHeight={fillViewportHeight}
    >
      <ReceivableTableHead>
        {columns.map((column) => {
          const align = resolveReceivableColumnAlign(column.key, schema);
          const isSortable =
            column.key !== "__status__" && sortableColumnIds.has(column.key);

          return (
            <th
              key={column.key}
              onClick={isSortable ? () => onSort(column.key) : undefined}
              className={cn(
                RECEIVABLE_TABLE_HEAD_CLASS,
                align === "right" ? "text-right" : "text-left",
                isSortable && "cursor-pointer",
              )}
            >
              <div
                className={cn(
                  "flex items-center gap-1.5",
                  align === "right" ? "justify-end" : "justify-start",
                  isSortable &&
                    "group hover:text-aera-charcoal transition-colors",
                )}
              >
                <span>{column.label}</span>
                {isSortable ? (
                  filters.sort === column.key ? (
                    <span className="text-(--aera-dt-accent) font-black drop-shadow-sm">
                      {filters.dir === "asc" ? "↑" : "↓"}
                    </span>
                  ) : (
                    <ArrowUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100 text-(--aera-dt-accent)" />
                  )
                ) : null}
              </div>
            </th>
          );
        })}
      </ReceivableTableHead>
      <tbody>
        {pageItems.map((item) => (
          <tr
            key={item.mergeKey}
            className={cn(
              RECEIVABLE_TABLE_ROW_CLASS,
              (() => {
                // Archive filter / already-expired rows: no urgency tint.
                if (hideExpiryUrgency) return null;
                const expiresMs = getReceivableExpiresAtMs(item);
                if (!expiresMs) return null;
                const remainingMs = expiresMs - Date.now();
                if (remainingMs <= 0) return null;
                if (remainingMs <= RECEIVABLE_ROW_EXPIRY_BLINK_THRESHOLD_MS) {
                  return RECEIVABLE_ROW_EXPIRY_BLINK_CLASS;
                }
                if (remainingMs <= RECEIVABLE_ROW_EXPIRY_BG_THRESHOLD_MS) {
                  return RECEIVABLE_ROW_EXPIRY_BG_CLASS;
                }
                return null;
              })(),
            )}
            onClick={() => onRowClick?.(item)}
          >
            {columns.map((column) => {
              const align = resolveReceivableColumnAlign(column.key, schema);

              return (
                <td
                  key={`${item.mergeKey}-${column.key}`}
                  className={cn(
                    RECEIVABLE_TABLE_CELL_CLASS,
                    column.key === "__status__" && "overflow-visible",
                    align === "right" ? "text-right" : "text-left",
                  )}
                >
                  <div
                    className={cn(
                      "flex h-full max-h-[36.5px] w-full min-w-0 items-center leading-5",
                      column.key === "__status__"
                        ? "overflow-visible"
                        : "overflow-hidden",
                      align === "right" ? "justify-end" : "justify-start",
                    )}
                  >
                    {column.key === "__status__" ? (
                      <ReceivableStatusBadges item={item} />
                    ) : (
                      <ReceivableFieldCell
                        fieldKey={column.key}
                        value={getReceivableFieldValue(item, column.key)}
                      />
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </ReceivableTableShell>
  );
}

function ReceivableHintButton() {
  const [hintOpen, setHintOpen] = useState(false);

  const handleHintOpenChange = (open: boolean) => {
    if (window.matchMedia("(hover: hover)").matches) {
      setHintOpen(open);
    }
  };

  const handleHintClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setHintOpen((open) => !open);
  };

  const hintButton = (
    <ConnectionLinkButton
      onClick={handleHintClick}
      ariaLabel="วิธีจัดการรายการค้าง"
      icon="alert"
      ariaExpanded={hintOpen}
    />
  );

  return (
    <TooltipProvider>
      <Tooltip
        delayDuration={0}
        open={hintOpen}
        onOpenChange={handleHintOpenChange}
      >
        <TooltipTrigger asChild>{hintButton}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="w-max max-w-[min(17rem,calc(100vw-2rem))] whitespace-normal wrap-break-word bg-aera-white text-left leading-relaxed"
        >
          <p>{RECEIVABLE_LINE_HINT}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ReceivableSummaryBadge({
  hasItems,
  label,
}: {
  hasItems: boolean;
  label: string;
}) {
  if (!hasItems) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-aera-greenlight px-2.5 py-0.5 text-xs font-semibold text-aera-green">
        ไม่มีรายการค้าง
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-aera-redlight px-2.5 py-0.5 text-xs font-semibold text-aera-red">
      {label}
    </span>
  );
}

function ReceivableTableEmptyState({
  hasSourceItems,
  filters,
  onClearSearch,
  schema,
}: {
  hasSourceItems: boolean;
  filters: TableFilters;
  onClearSearch: () => void;
  schema: WorkerSchema;
}) {
  const { emptyState } = schema;
  const searchQuery = filters.q?.trim() ?? "";
  const nameToken = filters.nameToken?.trim() ?? "";
  const emptyFilterLabel = searchQuery || nameToken;
  const hasNameOrSearch = Boolean(emptyFilterLabel);

  const title = hasNameOrSearch
    ? `ไม่พบ "${emptyFilterLabel}"`
    : hasSourceItems
      ? "ไม่พบรายการที่ตรงกับตัวกรอง"
      : emptyState.title;

  const body = hasNameOrSearch
    ? "ลองใช้คำค้นอื่น หรือเคลียร์ตัวกรองเพื่อดูรายการทั้งหมด"
    : hasSourceItems
      ? "ลองปรับตัวกรองเพื่อดูรายการทั้งหมด"
      : emptyState.body;

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 md:px-4 py-18 text-center">
      <span className="mb-3 text-5xl opacity-50 grayscale">
        {emptyState.icon}
      </span>
      <h3 className="mb-1 text-lg font-bold text-aera-charcoal">{title}</h3>
      <p className="mb-5 max-w-full text-sm leading-relaxed text-aera-gray md:max-w-[400px]">
        {body}
      </p>
      {hasNameOrSearch ? (
        <AeraButton
          variant="primary"
          fontWeight="medium"
          onClick={onClearSearch}
        >
          ล้างการค้นหา
        </AeraButton>
      ) : null}
    </div>
  );
}

function ReceivableTableLoadingState({
  filters,
  setFilter,
  containerWidth,
  schema,
  similarNameLabels,
}: {
  filters: TableFilters;
  setFilter: (key: string, value: string | null) => void;
  containerWidth?: number;
  schema: WorkerSchema;
  similarNameLabels?: string[];
}) {
  return (
    <>
      <FilterBar
        schema={schema}
        filters={filters}
        setFilter={setFilter}
        containerWidth={containerWidth}
        similarNameLabels={similarNameLabels}
      />
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <DataTableSkeleton schema={schema} className="h-full min-h-0" />
      </div>
      <DataTablePaginationSkeleton />
    </>
  );
}

function ReceivableLoadErrorState({ message }: { message: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 md:px-4 py-12 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-aera-redlight text-aera-red">
        <HandCoins className="h-6 w-6" strokeWidth={2} aria-hidden />
      </div>
      <p className="text-sm font-semibold text-aera-charcoal">{message}</p>
    </div>
  );
}

export function ReceivableOrdersPanel({
  workspaceId,
  workspace = null,
  containerWidth,
  receivableKind,
  schema = BillLinReceivableSchema,
  onMoneyChanged,
  onLoadingChange,
  onLoadFailure,
  fillViewportHeight,
  readOnly = false,
  canDelete = true,
}: {
  workspaceId: string;
  workspace?: Workspace | null;
  containerWidth?: number;
  receivableKind: ReceivableTabKind;
  schema?: WorkerSchema;
  /** Role cannot write (caller's can()) — drawer actions disabled. */
  readOnly?: boolean;
  /** Role may use “ลบรายการ” (caller's can()). */
  canDelete?: boolean;
  /** After match/create/unmatch — refresh ledger tabs (รายรับ) immediately. */
  onMoneyChanged?: () => void | Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
  onLoadFailure?: (failed: boolean) => void;
  /** When false, grow with rows instead of filling remaining viewport height. */
  fillViewportHeight?: boolean;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [filters, setFilters] = useState<TableFilters>(() =>
    createDefaultReceivableFilters(schema.defaultSort),
  );
  const listQuery = useMemo(
    () => ({
      kind: receivableKind as ReceivableKind,
      dateFrom: filters.from,
      dateTo: filters.to,
      q: filters.q,
      status: filters.status,
    }),
    [receivableKind, filters.from, filters.to, filters.q, filters.status],
  );
  const { itemsByKind, ownerUserId, isLoading, error, refetch } =
    useWorkspaceReceivableDetails(workspaceId, workspace, listQuery);

  useEffect(() => {
    onLoadingChange?.(isLoading);
    onLoadFailure?.(!isLoading && Boolean(error));
  }, [error, isLoading, onLoadFailure, onLoadingChange]);

  const scopedItemsByKind = useMemo(() => {
    const ws = workspaceId.trim();
    if (!ws) return itemsByKind;
    const scope = (
      items: ReceivableListItem[],
      kind: ReceivableKind,
    ) =>
      items.filter((item) => {
        const itemWs = (item.workspaceId || "").trim();
        // Untagged Redis rows are legacy current-shop items — keep them.
        if (!itemWs) return true;
        return itemWs === ws;
      });
    return {
      announcement: scope(itemsByKind.announcement ?? [], "announcement"),
      address: scope(itemsByKind.address ?? [], "address"),
      slip: scope(itemsByKind.slip ?? [], "slip"),
    } satisfies Record<ReceivableKind, ReceivableListItem[]>;
  }, [itemsByKind, workspaceId]);

  const refreshAfterMoneyChange = useCallback(async () => {
    // Force + silent: don't reuse a pre-match in-flight fetch, don't flash skeleton.
    // First read is awaited; background retries cover rare Redis lag.
    await refetchReceivablePendingWithRetry(refetch, {
      silent: true,
      force: true,
      retries: 2,
      retryDelayMs: 180,
      reason: "money_changed",
    });
    await Promise.resolve(onMoneyChanged?.());
    // รายรับ list projects payments and can lag the match response / sheet upsert.
    // Nudge again so the tab shows the new row without a manual page refresh.
    window.setTimeout(() => {
      void Promise.resolve(onMoneyChanged?.());
    }, 500);
    window.setTimeout(() => {
      void Promise.resolve(onMoneyChanged?.());
    }, 1500);
  }, [refetch, onMoneyChanged]);
  const kindItems = scopedItemsByKind[receivableKind as ReceivableKind] ?? [];
  // Both kinds stay populated on ยอดค้าง/สลิปค้าง so the drawer match pickers
  // (ตัดยอดค้าง / จับคู่การชำระเงิน) are not emptied by the active tab.
  const slipItems = useMemo(
    () => filterReceivableMoneyCandidates(scopedItemsByKind.slip ?? []),
    [scopedItemsByKind.slip],
  );
  const announcementItems = useMemo(
    () => filterReceivableMoneyCandidates(scopedItemsByKind.announcement ?? []),
    [scopedItemsByKind.announcement],
  );
  const statusFilter = filters.status?.trim() ?? "";
  const summarySourceItems = useMemo(() => {
    // Keep header summary aligned with default table: archived statuses stay out
    // until รายการที่ลบ/หมดอายุ is selected in the status filter.
    if (statusFilter === "รายการที่ลบ" || statusFilter === "หมดอายุ") {
      return kindItems;
    }
    return kindItems.filter((item) => !isReceivableArchivedDisplayStatus(item));
  }, [kindItems, statusFilter]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<ReceivableListItem | null>(
    null,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerInitialTab, setDrawerInitialTab] = useState<
    "details" | "corrections" | "resplit" | "history" | null
  >(null);

  const fwSearch = useFinancialWorkSearch();
  const setFilter = useCallback((key: string, value: string | null) => {
    setFilters((current) => ({ ...current, [key]: value }));
    if (key === "q") fwSearch?.setSearchQuery(value ?? "");
  }, [fwSearch]);
  useBindFinancialWorkSearch(filters.q, setFilter);

  const handleSort = useCallback((colId: string) => {
    setFilters((current) => {
      const isSame = current.sort === colId;
      const newDir = isSame && current.dir === "desc" ? "asc" : "desc";
      return { ...current, sort: colId, dir: newDir };
    });
  }, []);

  const filteredItems = useMemo(
    () => filterAndSortReceivableItems(kindItems, filters),
    [filters, kindItems],
  );
  const summary = useMemo(
    () =>
      summarizeReceivableItemsByKind(
        filteredItems,
        receivableKind as ReceivableKind,
      ),
    [filteredItems, receivableKind],
  );
  const similarNameLabels = useMemo(
    () =>
      filterAndSortReceivableItems(kindItems, {
        ...filters,
        q: null,
        nameToken: null,
      }).map((item) => item.title),
    [filters, kindItems],
  );

  const skipKindResetOnceRef = useRef(true);
  const autoOpenedDeepLinkIdRef = useRef<string | null>(null);

  // Deep-link: open drawer once by `recordId` / `highlight`.
  // Do not re-run on close (`isDrawerOpen`) or leftover URL params will reopen it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!filteredItems.length) return;
    const url = new URL(window.location.href);
    const id =
      url.searchParams.get("recordId")?.trim() ||
      url.searchParams.get("highlight")?.trim() ||
      "";
    if (!id) return;
    if (autoOpenedDeepLinkIdRef.current === id) return;
    const match = filteredItems.find((item) => {
      const orderId = getReceivableOrderId(item);
      return (
        orderId === id ||
        item.mergeKey === id ||
        item.key === id ||
        (item.receivableId || "").trim() === id
      );
    });
    if (!match) return;
    const tab = url.searchParams.get("drawerTab");
    setDrawerInitialTab(
      tab === "history" ||
        tab === "corrections" ||
        tab === "resplit" ||
        tab === "details"
        ? tab
        : null,
    );
    autoOpenedDeepLinkIdRef.current = id;
    setSelectedItem(match);
    setIsDrawerOpen(true);
  }, [filteredItems]);

  useEffect(() => {
    if (skipKindResetOnceRef.current) {
      skipKindResetOnceRef.current = false;
      return;
    }
    setFilters(createDefaultReceivableFilters(schema.defaultSort));
    setPageIndex(0);
    setSelectedItem(null);
    setIsDrawerOpen(false);
    setDrawerInitialTab(null);
  }, [receivableKind, schema.defaultSort.col, schema.defaultSort.dir]);

  useEffect(() => {
    setPageIndex(0);
  }, [filters]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filteredItems.length / RECEIVABLE_PAGE_SIZE),
    );
    setPageIndex((current) => Math.min(current, totalPages - 1));
  }, [filteredItems.length]);

  const hasFilteredItems = filteredItems.length > 0;
  const hasSourceItems = summarySourceItems.length > 0;
  const statusLabel = summary.cardSubtitle;
  const displayCount = filteredItems.length;

  const selectedIndex = useMemo(() => {
    if (!selectedItem) return -1;
    return filteredItems.findIndex(
      (item) => item.mergeKey === selectedItem.mergeKey,
    );
  }, [filteredItems, selectedItem]);

  const drawerItem = useMemo(() => {
    if (!selectedItem) return null;
    return (
      kindItems.find((item) => item.mergeKey === selectedItem.mergeKey) ??
      selectedItem
    );
  }, [kindItems, selectedItem]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isDrawerOpen) return;
    if (!drawerItem) return;
    const url = new URL(window.location.href);
    const id = getReceivableOrderId(drawerItem) || drawerItem.mergeKey;
    if (id) url.searchParams.set("recordId", id);
    replaceUrlWithoutNavigation(url);
  }, [drawerItem?.mergeKey, isDrawerOpen]);

  const handleRowClick = useCallback((item: ReceivableListItem) => {
    setSelectedItem(item);
    setIsDrawerOpen(true);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const id = getReceivableOrderId(item) || item.mergeKey;
    if (id) url.searchParams.set("recordId", id);
    url.searchParams.delete("highlight");
    url.searchParams.delete("drawerTab");
    url.searchParams.delete("modal");
    replaceUrlWithoutNavigation(url);
  }, []);

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    setIsDrawerOpen(open);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!open) {
      const dismissed =
        url.searchParams.get("recordId")?.trim() ||
        url.searchParams.get("highlight")?.trim() ||
        "";
      if (dismissed) autoOpenedDeepLinkIdRef.current = dismissed;
      url.searchParams.delete("recordId");
      url.searchParams.delete("highlight");
      url.searchParams.delete("drawerTab");
      url.searchParams.delete("modal");
      replaceUrlWithoutNavigation(url);
      router.replace(`${pathname}${url.search}${url.hash}`, { scroll: false });
    }
  }, [pathname, router]);

  const handleDrawerExited = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handlePrevItem = useCallback(() => {
    if (selectedIndex <= 0) return;
    setSelectedItem(filteredItems[selectedIndex - 1]);
  }, [filteredItems, selectedIndex]);

  const handleNextItem = useCallback(() => {
    if (selectedIndex < 0 || selectedIndex >= filteredItems.length - 1) return;
    setSelectedItem(filteredItems[selectedIndex + 1]);
  }, [filteredItems, selectedIndex]);

  return (
    <div
      className={
        fillViewportHeight === false
          ? "flex min-w-0 shrink-0 flex-col text-aera-charcoal"
          : "flex min-h-0 min-w-0 flex-1 flex-col text-aera-charcoal max-[899px]:h-auto max-[899px]:flex-none"
      }
      style={RECEIVABLE_TABLE_THEME}
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-aera-lightgray bg-aera-white/80 px-3 md:px-4 py-2.5 text-sm">
        <div className="flex min-w-0 min-h-8.5 flex-wrap items-center gap-3">
          <span className="font-semibold text-aera-charcoal">สรุป</span>
          {isLoading ? (
            <span className="text-aera-gray text-xs font-semibold">
              กำลังโหลด…
            </span>
          ) : error ? (
            <span className="text-aera-red text-xs font-semibold">
              โหลดไม่สำเร็จ
            </span>
          ) : (
            <>
              <span className="text-aera-charcoal">
                {displayCount.toLocaleString("th-TH")} รายการ
              </span>
              <div className="flex items-center gap-2">
                <ReceivableSummaryBadge
                  hasItems={hasFilteredItems}
                  label={statusLabel}
                />
                {hasFilteredItems ? <ReceivableHintButton /> : null}
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className={
          fillViewportHeight === false
            ? "relative z-20 flex min-w-0 shrink-0 flex-col"
            : "relative z-20 flex min-h-0 min-w-0 flex-1 flex-col max-[899px]:h-auto max-[899px]:flex-none"
        }
      >
        {isLoading ? (
          <ReceivableTableLoadingState
            filters={filters}
            setFilter={setFilter}
            containerWidth={containerWidth}
            schema={schema}
            similarNameLabels={similarNameLabels}
          />
        ) : error ? (
          <ReceivableLoadErrorState message="ไม่สามารถโหลดรายการค้างได้ กรุณาลองใหม่อีกครั้ง" />
        ) : (
          <>
            <FilterBar
              schema={schema}
              filters={filters}
              setFilter={setFilter}
              containerWidth={containerWidth}
              similarNameLabels={similarNameLabels}
            />
            <div
              className={
                fillViewportHeight === false
                  ? "relative flex flex-col overflow-visible"
                  : "relative flex min-h-0 flex-1 flex-col overflow-hidden max-[899px]:h-auto max-[899px]:flex-none max-[899px]:overflow-visible"
              }
            >
              {filteredItems.length > 0 ? (
                <ReceivableDynamicTable
                  items={filteredItems}
                  pageIndex={pageIndex}
                  receivableKind={receivableKind as ReceivableKind}
                  schema={schema}
                  filters={filters}
                  onSort={handleSort}
                  onRowClick={handleRowClick}
                  fillViewportHeight={fillViewportHeight !== false}
                />
              ) : (
                <ReceivableTableEmptyState
                  hasSourceItems={hasSourceItems}
                  filters={filters}
                  onClearSearch={() => {
                    setFilter("q", null);
                    setFilter("nameToken", null);
                  }}
                  schema={schema}
                />
              )}
            </div>
            <ReceivableTablePagination
              pageIndex={pageIndex}
              totalItems={filteredItems.length}
              onPageChange={setPageIndex}
            />
          </>
        )}
      </div>

      <ReceivableRecordDrawer
        open={isDrawerOpen}
        onOpenChange={handleDrawerOpenChange}
        item={drawerItem}
        slipItems={slipItems}
        announcementItems={announcementItems}
        ownerUserId={ownerUserId}
        workspaceId={workspaceId}
        onRefresh={refreshAfterMoneyChange}
        onItemSynced={setSelectedItem}
        readOnly={readOnly}
        canDelete={canDelete}
        onPrev={selectedIndex > 0 ? handlePrevItem : undefined}
        onNext={
          selectedIndex >= 0 && selectedIndex < filteredItems.length - 1
            ? handleNextItem
            : undefined
        }
        position={
          selectedIndex >= 0
            ? { current: selectedIndex + 1, total: filteredItems.length }
            : undefined
        }
        initialTab={drawerInitialTab}
        onTabChange={(tab) => {
          if (typeof window === "undefined") return;
          const url = new URL(window.location.href);
          if (tab === "history" || tab === "corrections" || tab === "resplit") {
            url.searchParams.set("drawerTab", tab);
          } else {
            url.searchParams.delete("drawerTab");
          }
          replaceUrlWithoutNavigation(url);
        }}
        onMatchModalOpenChange={(open, kind) => {
          if (typeof window === "undefined") return;
          const url = new URL(window.location.href);
          if (open) url.searchParams.set("modal", kind);
          else if (url.searchParams.get("modal") === kind)
            url.searchParams.delete("modal");
          replaceUrlWithoutNavigation(url);
        }}
        onExited={handleDrawerExited}
      />
    </div>
  );
}

/** @deprecated Use ReceivableOrdersPanel inside BillinDataView */
export function ReceivableOrdersSection({
  workspaceId,
  workspace = null,
  sectionRef,
}: {
  workspaceId: string;
  workspace?: Workspace | null;
  sectionRef?: Ref<HTMLElement>;
}) {
  return (
    <section
      ref={sectionRef}
      id="billin-receivable"
      className="scroll-mt-4 flex min-h-[min(400px,calc(100dvh-22rem))] min-w-0 flex-col overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white text-aera-charcoal"
      style={RECEIVABLE_TABLE_THEME}
    >
      <div className="flex shrink-0 items-center gap-2 md:gap-3 border-b border-aera-lightgray p-3 md:p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-redlight text-aera-red">
          <HandCoins className="h-5 w-5" strokeWidth={2} aria-hidden />
        </div>
        <p className="truncate text-sm font-semibold text-aera-charcoal">
          รายการยอดค้าง
        </p>
      </div>

      <ReceivableOrdersPanel
        workspaceId={workspaceId}
        workspace={workspace}
        receivableKind="announcement"
        schema={BillLinReceivableSchema}
      />
    </section>
  );
}
